// Per-Block-instance form section rendered inside the Email editor's
// left pane. Per spec/build-plan.md P-032 row deliverables: per-Block
// "Include in this email" toggle + auto-hide visual feedback +
// inputsSchema-driven Form mount. Mirrors `<BlockInstanceCard>`
// (P-031 / D-093) shape but for the Email-editing flow (inputs over
// options).
//
// **Two-dimensional state — `included` and `autoHide`.** Both can fire
// independently. `included === false` is staff-driven (checkbox);
// `autoHide === true` is content-driven (per-Block predicate, e.g.
// "events.length === 0"). UI hierarchy:
// - included=false → gray dim + checkbox unchecked + "Excluded" pill.
// - autoHide=true  → amber border + "Auto-hidden — empty content" pill.
// - both           → gray + amber + both pills (degenerate case;
//   staff has explicitly excluded a Block whose content would auto-hide
//   anyway).
// The form fields stay editable in both states; staff can toggle back
// without losing data (D-077: `included === false` skips render but the
// stored inputs survive).
//
// **Big-3 cross-schema dispatch (deprecated at P-037.22 D-130).** The
// former rule — "when `blockId === 'event-card-group'` has
// `options.style === 'big-3'`, render the events array as 3 fixed slots
// labeled 'Meeting 1/2/3' via `<Form arrayOverrides>`" — is gone with
// the `style` Option drop. No Block currently uses an `arrayOverrides`
// path; the prop remains supported on `<Form>` for V2 reintroductions
// (the cross-schema dispatch primitive is intentionally retained per
// P-032's design intent).
//
// **`autoHide` invocation safety.** `def.autoHide?.({inputs})` assumes
// parsed-shape inputs (e.g. `inputs.events.length === 0` would throw
// on undefined `events`). At the Email editor the inputs are mid-edit
// and may not parse. We `safeParse` against the schema first and call
// autoHide only on a successful parse; on failure we fall through to
// "not auto-hidden" (the editor surfaces inline validation errors via
// the Form for the same fields, so staff knows why).

import type { z } from 'zod'

import { secondaryButtonClassIconish } from '../button-classes'
import { Form } from '../form/form'
import type { EmailBlockInstance } from '../../store/schemas'

// `autoHide`'s parameter is typed `any` (not `unknown`) to remain
// assignable from the registry's union of concrete BlockDefinitions.
// `BlockDefinition<Options, Inputs>['autoHide']` is `(data: { inputs:
// z.infer<Inputs> }) => boolean`; under strict variance the union
// would not assign to `(data: { inputs: unknown }) => boolean` because
// the structural-class match needs `any`-bivariance to collapse the
// per-Block narrow `inputs` types. Same `any`-relaxation pattern as
// the registry itself (D-080) and render-block.ts's widening cast
// (D-077).
interface BlockSectionDef {
  readonly id: string
  readonly label: string
  readonly inputsSchema: z.ZodTypeAny
  readonly defaultInputs: unknown
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly autoHide?: (data: { inputs: any }) => boolean
}

interface BlockSectionProps {
  readonly instance: EmailBlockInstance
  readonly def: BlockSectionDef
  // P-037.30 / D-138 — Block-manipulation controls (Item 1). Mirrors
  // `<BlockInstanceCard>`'s props on the Template-side. Always
  // surfaced regardless of `included` / `autoHide` state.
  readonly canMoveUp: boolean
  readonly canMoveDown: boolean
  readonly onMoveUp: () => void
  readonly onMoveDown: () => void
  readonly onRemove: () => void
  readonly onIncludedChange: (included: boolean) => void
  readonly onInputsChange: (next: unknown) => void
}

function isAutoHidden(def: BlockSectionDef, inputs: unknown): boolean {
  if (def.autoHide === undefined) return false
  const parsed = def.inputsSchema.safeParse(inputs)
  if (!parsed.success) return false
  return def.autoHide({ inputs: parsed.data }) === true
}

export function BlockSection({
  instance,
  def,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  onRemove,
  onIncludedChange,
  onInputsChange,
}: BlockSectionProps) {
  const included = instance.included
  const autoHidden = isAutoHidden(def, instance.inputs)
  const checkboxId = `block-${instance.instanceId}-included`

  const sectionClass = [
    'rounded border bg-white',
    included ? '' : 'opacity-60',
    autoHidden ? 'border-amber-300' : 'border-gray-200',
  ]
    .filter((c) => c !== '')
    .join(' ')

  return (
    <li class={sectionClass} aria-label={`${def.label} block`}>
      <header class="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2">
        <div class="min-w-0">
          <label
            for={checkboxId}
            class="flex items-center gap-2 text-sm font-medium text-gray-900"
          >
            <input
              id={checkboxId}
              type="checkbox"
              checked={included}
              onChange={(e) => onIncludedChange((e.currentTarget as HTMLInputElement).checked)}
              class="h-4 w-4 rounded border-gray-300"
            />
            <span class="truncate">{def.label}</span>
          </label>
          <code class="ml-6 text-xs text-gray-500">{def.id}</code>
        </div>
        <div class="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {!included && (
            <span class="rounded bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
              Excluded
            </span>
          )}
          {autoHidden && (
            <span class="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              Auto-hidden — empty content
            </span>
          )}
          <div class="flex shrink-0 gap-1">
            <button
              type="button"
              onClick={onMoveUp}
              disabled={!canMoveUp}
              aria-label="Move block up"
              class={secondaryButtonClassIconish}
            >
              ↑
            </button>
            <button
              type="button"
              onClick={onMoveDown}
              disabled={!canMoveDown}
              aria-label="Move block down"
              class={secondaryButtonClassIconish}
            >
              ↓
            </button>
            <button
              type="button"
              onClick={onRemove}
              aria-label="Remove block"
              class="rounded border border-red-300 bg-white px-2 py-1 text-sm text-red-700 hover:bg-red-50"
            >
              🗑
            </button>
          </div>
        </div>
      </header>
      <div class="px-3 py-3">
        <Form
          schema={def.inputsSchema}
          value={instance.inputs ?? def.defaultInputs}
          onChange={onInputsChange}
          idPrefix={`block-${instance.instanceId}-inputs`}
          blockId={def.id}
        />
      </div>
    </li>
  )
}
